import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "../../components/ui/use-toast";
import { ClipLoader } from "react-spinners";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "../../components/ui/dialog";
import ProductForm from "../../components/ProductForm";
import { Input } from "../../components/ui/input";
import { IoSearchOutline } from "react-icons/io5";
import { AiOutlineEdit } from "react-icons/ai";
import { MdKeyboardArrowRight } from "react-icons/md";
import img from "../../pics/landImg.jpeg";
import { useCheckClientProducts } from "../../migration-api/api-hooks/clients";

function OnboardProducts() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [clientProducts, setClientProducts] = useState([]);
  const [clientEmail, setClientEmail] = useState("");
  const [localProduct, setLocalProduct] = useState(null);

  // Define email validation schema
  const emailSchema = z.string().email({
    message: "Only valid email is allowed",
  });

  // Use the custom hook for checking client products
  const { checkClientProducts, isLoading: isChecking } = useCheckClientProducts(
    {
      onSuccess: (data) => {
        const products = data?.data?.products || [];
        setClientProducts(products);

        if (products.length === 0) {
          toast({
            title: "No Products Found",
            description: "This client has no products available.",
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: `Found ${products.length} product(s)`,
          });
        }
      },
      onError: (error) => {
        // Clear the products when error occurs
        setClientProducts([]);

        if (error.code === "ERR_NETWORK") {
          navigate("/", { replace: true });
        } else if (
          error.response?.status === 401 ||
          error.response?.status === 403
        ) {
          navigate("/", { replace: true });
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description:
              error?.response?.data?.message || "Failed to fetch products",
          });
        }
      },
    }
  );

  // Handle opening edit dialog
  const handleOpen = (productData) => {
    setOpen(true);
    setLocalProduct({
      ...productData,
      client_email: clientEmail,
      descriptions: productData?.description,
      gtin: productData?.gtin,
      // gtin: productData?.gtin?.replace(/^0+/, ""),
    });
  };

  // Handle search
  const handleGs1Search = async () => {
    if (!clientEmail || clientEmail.trim() === "") return;
	
	// Validate email using Zod
    const result = emailSchema.safeParse(clientEmail);
    
    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: result.error.errors[0].message,
      });
      return;
    }
	
    checkClientProducts({ client_email: clientEmail });
  };

  // Handle email input change
  const handleChange = (e) => {
	const email = e.target.value;
    setClientEmail(email);
  };

  const isSearchDisabled = !clientEmail || clientEmail?.trim() === "";

  return (
    <section className="relative w-full min-h-screen">
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-primary text-3xl my-5 font-medium">
            Update your products
          </h2>

          <div className="space-y-2">
            <Link
              to={"/onboard-locations"}
              className="rounded-lg px-4 py-1 bg-primary text-white flex items-center gap-2 cursor-pointer"
              replace
            >
              Next Step <MdKeyboardArrowRight />
            </Link>
            <small>Confirm Locations</small>
          </div>
        </div>

        {/* Search Section */}
        <div className="mt-4">
          <p className="mb-2">Enter client email here</p>

          <div className="relative flex items-center">
            <Input
              type="email"
              placeholder="johndoe@mail.com"
              name="client_email"
              autoComplete="off"
              containerStyle="w-full"
              className="pr-11"
              value={clientEmail}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isSearchDisabled) {
                  handleGs1Search();
                }
              }}
            />
            <button
              type="button"
              className="absolute right-2 bg-primary text-white rounded-md p-1 disabled:bg-primary/60"
              onClick={handleGs1Search}
              disabled={isChecking || isSearchDisabled}
            >
              {isChecking ? (
                <ClipLoader color="white" size={14} />
              ) : (
                <IoSearchOutline />
              )}
            </button>
          </div>
          <small>
            Click the search icon to see all your available products, and update
            them.
          </small>
        </div>

        {/* Products Table */}
        <div className="relative">
          {isChecking ? (
            <div className="p-[40px] h-[20vh] place-content-center text-center z-50">
              <ClipLoader color="primary" size={35} />
              <p>Loading products</p>
            </div>
          ) : (
            <section className="mt-8">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Description</TableHead>
                    <TableHead>GTIN</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientProducts?.length > 0 ? (
                    clientProducts.map((product, i) => (
                      <TableRow key={product.gtin || i}>
                        <TableCell>{product.description}</TableCell>
                        <TableCell>{product.gtin}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleOpen(product)}
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <AiOutlineEdit /> edit
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <h1 className="text-center h-[20vh] place-content-center text-gray-500">
                          {clientEmail
                            ? "NO PRODUCTS FOUND"
                            : "Enter a client email to search for products"}
                        </h1>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </section>
          )}
        </div>
      </div>

      {/* Edit Product Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogDescription>
              <ProductForm
                clientProduct={localProduct}
                isOnboarding={true}
                onComplete={() => {
                  setOpen(false);
                  // Optionally refresh products after edit
                  if (clientEmail) {
                    checkClientProducts({ client_email: clientEmail });
                  }
                }}
              />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <section className="absolute bottom-0 w-full flex flex-col justify-center items-center gap-4">
        <p className="text-primary text-[9px]">
          Copyright &copy; {new Date().getFullYear()}
        </p>
        <img src={img} alt="" className="max-h-[100px] max-w-[70dvw] mx-auto" />
      </section>
    </section>
  );
}

export default OnboardProducts;
